import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Link,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import OpenInNew from "@mui/icons-material/OpenInNew";
import LocationOn from "@mui/icons-material/LocationOn";
import CloudOutlined from "@mui/icons-material/CloudOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import ArticleOutlined from "@mui/icons-material/ArticleOutlined";
import ChecklistRtl from "@mui/icons-material/ChecklistRtl";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import FolderOpenOutlined from "@mui/icons-material/FolderOpenOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import TagOutlined from "@mui/icons-material/TagOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useGetInvestorAssetSummaryQuery,
  useLazyGetInvestorAssetByIdQuery,
} from "../../redux/api/authApi";
import InvestorInvestModal from "./InvestorInvestModal";
import InvestorAssetManagedView from "./InvestorAssetManagedView";
// import { DRAWER_WIDTH } from "../../theme/dashboardShell";
import { getInvestorApiBase } from "../../utils/investorApiBase";
import noImgPlaceholder from "../../Assets/no-img-placeholder.png";
import { isRealEstateCategory } from "../../utils/assetCategory";
import { cleanLocation } from "../../utils/location";

const TEAL = "#00bfa5";
const TEAL_DARK = "#0d9488";

/** Resolve relative file paths to absolute URLs */
function resolveInvestorMediaUrl(fileUrl) {
  if (!fileUrl) return "";
  const s = String(fileUrl).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const path = s.replace(/^\//, "");
  const API_BASE = getInvestorApiBase();
  if (!API_BASE) return path;
  if (path.startsWith("uploads/")) {
    return `${API_BASE}/${path}`;
  }
  return `${API_BASE}/uploads/${path}`;
}

function formatSar(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-SA", { maximumFractionDigits: 0 }).format(x);
}

function formatTokens(n) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return "0";
  return new Intl.NumberFormat("en-US").format(Math.round(x));
}

function formatAddress(addr) {
  if (!addr || typeof addr !== "object") return "";
  const parts = [
    addr.streetAddress,
    addr.city?.name,
    addr.state?.name,
    addr.country?.name,
    addr.zipCode,
  ].filter(Boolean);
  return parts.join(", ");
}

const DETAIL_TABS = [
  { id: "overview", label: "Overview", Icon: ArticleOutlined },
  { id: "features", label: "Features", Icon: ChecklistRtl },
  { id: "specifications", label: "Specifications", Icon: TuneOutlined },
  { id: "location", label: "Location", Icon: PlaceOutlined },
  { id: "documents", label: "Documents", Icon: FolderOpenOutlined },
  { id: "verification", label: "Verification", Icon: VerifiedUserOutlined },
];

const REAL_ESTATE_ONLY_TAB_IDS = new Set(["features", "specifications", "location"]);

// function documentTypeLabel(type) {
//   const t = String(type || "").toLowerCase();
//   if (t.includes("legal")) return "Legal";
//   if (t.includes("financial") || t.includes("valuation")) return "Financial";
//   if (t.includes("technical") || t.includes("spv")) return "Technical";
//   if (t === "titledeed" || t === "title deed") return "Title Deed";
//   return "Document";
// }

export default function InvestorAssetDetail() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isManagedView = searchParams.get("managed") === "1";
  const [tab, setTab] = useState("overview");
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [investOpen, setInvestOpen] = useState(false);
  const [showFloatingInvest, setShowFloatingInvest] = useState(false);
  const detailTabsSectionRef = useRef(null);
  const [primaryInvestBtn, setPrimaryInvestBtn] = useState(null);

  useEffect(() => {
    if (!primaryInvestBtn) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFloatingInvest(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(primaryInvestBtn);
    return () => observer.disconnect();
  }, [primaryInvestBtn]);

  const {
    data: summaryRes,
    isLoading: sumLoading,
    isFetching: sumFetching,
    isError: sumError,
    error: sumErr,
    refetch: refetchSummary,
  } = useGetInvestorAssetSummaryQuery(assetId, {
    skip: !assetId || isManagedView,
  });

  const summaryData = summaryRes?.data?.data ?? summaryRes?.data;
  const summaryStatusFunded =
    String(summaryData?.asset?.status ?? "")
      .trim()
      .toLowerCase() === "funded";

  const [
    getInvestorAssetById,
    { data, isLoading, isFetching, isUninitialized, isError, error },
  ] = useLazyGetInvestorAssetByIdQuery();

  useEffect(() => {
    if (!assetId || isManagedView) return;
    if (sumLoading || sumFetching) return;
    if (sumError) return;
    if (!summaryData?.asset) return;
    if (summaryStatusFunded) {
      navigate(`/investor/viewinvest/${assetId}?managed=1`, {
        replace: true,
        state: { viewTab: "my" },
      });
      return;
    }
    getInvestorAssetById(assetId);
  }, [
    assetId,
    isManagedView,
    sumLoading,
    sumFetching,
    sumError,
    summaryData,
    summaryStatusFunded,
    getInvestorAssetById,
    navigate,
  ]);

  const loadAsset = useCallback(async () => {
    if (!assetId || isManagedView) return;
    const result = await refetchSummary();
    const latest =
      result?.data?.data?.data ?? result?.data?.data ?? result?.data;
    const funded =
      String(latest?.asset?.status ?? "")
        .trim()
        .toLowerCase() === "funded";
    if (funded) {
      navigate(`/investor/viewinvest/${assetId}?managed=1`, {
        replace: true,
        state: { viewTab: "my" },
      });
      return;
    }
    getInvestorAssetById(assetId);
  }, [assetId, isManagedView, refetchSummary, getInvestorAssetById, navigate]);

  const asset = data?.data;

  const isRealEstate = useMemo(() => isRealEstateCategory(asset), [asset]);
  const mintTxHash = asset?.deployment?.mintTxHash || null;

  const visibleDetailTabs = useMemo(() => {
    const tabs = DETAIL_TABS.filter((t) => !REAL_ESTATE_ONLY_TAB_IDS.has(t.id) || isRealEstate);
    if (mintTxHash) {
      tabs.push({ id: "blockchain", label: "Blockchain", Icon: TagOutlined });
    }
    return tabs;
  }, [isRealEstate, mintTxHash]);

  useEffect(() => {
    if (!isRealEstate && REAL_ESTATE_ONLY_TAB_IDS.has(tab)) {
      setTab("overview");
    }
    if (tab === "blockchain" && !mintTxHash) {
      setTab("overview");
    }
  }, [isRealEstate, tab, mintTxHash]);

  const handleManageDocuments = () => {
    setTab("documents");
    requestAnimationFrame(() => {
      detailTabsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const mainUrl = useMemo(() => {
    if (!asset?.assetImages) return "";
    const main = asset.assetImages.mainImage?.fileUrl;
    const gallery = asset.assetImages.galleryImages;
    if (galleryIndex > 0 && Array.isArray(gallery) && gallery[galleryIndex - 1]?.fileUrl) {
      return resolveInvestorMediaUrl(gallery[galleryIndex - 1].fileUrl);
    }
    return resolveInvestorMediaUrl(main);
  }, [asset, galleryIndex]);

  const thumbSources = useMemo(() => {
    if (!asset?.assetImages?.mainImage?.fileUrl) return [];
    const main = { key: "main", url: resolveInvestorMediaUrl(asset.assetImages.mainImage.fileUrl) };
    const rest = (asset.assetImages.galleryImages || []).map((g, i) => ({
      key: `g-${i}`,
      url: resolveInvestorMediaUrl(g.fileUrl),
    }));
    return [main, ...rest].filter((x) => x.url);
  }, [asset]);

  const tokenEconomics = asset?.tokenEconomics || {};
  const financial = asset?.financialDetails || {};
  const basic = asset?.basicInfo || {};
  const totalSupply = Number(tokenEconomics.totalSupply) || 0;
  const tokensSold = Number(tokenEconomics.tokensSold) || 0;
  const available = Math.max(0, totalSupply - tokensSold);
  const soldPct = totalSupply > 0 ? Math.min(100, (tokensSold / totalSupply) * 100) : 0;

  if (!assetId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Missing asset.</Alert>
      </Box>
    );
  }

  if (isManagedView) {
    return <InvestorAssetManagedView />;
  }

  if (sumLoading || sumFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  if (sumError || !summaryData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => refetchSummary()}>
          {sumErr?.data?.message || sumErr?.error || "Could not load asset summary."}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/investor/viewinvest")} startIcon={<ArrowBack />}>
          Back to assets
        </Button>
      </Box>
    );
  }

  if (summaryStatusFunded) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  if (isUninitialized || isLoading || isFetching) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
      <CircularProgress sx={{ color: TEAL }} />
    </Box>
  );
}

// if (isError || !asset) {
//   return (
//     <Box sx={{ p: 3 }}>
//       <Alert severity="error">
//         {error?.data?.message || error?.error || "Could not load asset."}
//       </Alert>

//       <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
//         <Button onClick={() => navigate("/investor/viewinvest")} startIcon={<ArrowBack />}>
//           Back to assets
//         </Button>

//         <Button variant="outlined" onClick={loadAsset}>
//           Retry
//         </Button>
//       </Stack>
//     </Box>
//   );
// }

if (isError || !asset) {
  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">
        {error?.data?.message || error?.error || "Could not load asset."}
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button onClick={() => navigate("/investor/viewinvest")} startIcon={<ArrowBack />}>
          Back to assets
        </Button>

        <Button variant="outlined" onClick={loadAsset}>
          Retry
        </Button>
      </Stack>
    </Box>
  );
}

  // if (isError || !asset) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Alert severity="error" onClose={() => loadAsset()}>
  //         {error?.data?.message || error?.error || "Could not load asset."}
  //       </Alert>
  //       <Button sx={{ mt: 2 }} onClick={() => navigate("/investor/viewinvest")} startIcon={<ArrowBack />}>
  //         Back to assets
  //       </Button>
  //     </Box>
  //   );
  // }

  const categoryName = asset.assetCategory?.name || "—";
  const isActive = String(asset.status || "").toLowerCase() === "active";
  const verification = asset.verification || {};
  const documents = Array.isArray(asset.documents) ? asset.documents : [];
  const features = Array.isArray(basic.featuresAndAmenities) ? basic.featuresAndAmenities : [];
  const specs = basic.propertySpecifications || {};

  return (
    <Box sx={{ minHeight: "100vh", pb: 10 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/investor/viewinvest")}
        sx={{ mb: 2, textTransform: "none", color: "#475569", fontWeight: 600 }}
      >
        Back to Assets
      </Button>

      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              {basic.assetName || "Asset"}
            </Typography>
            <Chip label={categoryName} size="small" sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700 }} />
          </Stack>
          {cleanLocation(asset.location) || formatAddress(basic.completeAddress) ? (
            <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ mt: 1 }}>
              <LocationOn sx={{ fontSize: 20, color: "#94a3b8" }} />
              <Typography variant="body2" color="text.secondary">
                {cleanLocation(asset.location) || formatAddress(basic.completeAddress)}
              </Typography>
            </Stack>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
          {/* <Chip
            icon={<Verified sx={{ color: "#16a34a !important" }} />}
            label="DLD Verified"
            size="small"
            sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0" }}
          /> */}
          <Chip
            icon={<CloudOutlined sx={{ fontSize: "18px !important" }} />}
            label="Deployed on Ethereum"
            size="small"
            variant="outlined"
            sx={{ bgcolor: "#fff", p: 1 }}
          />
          <Chip
            label={asset.status || "Active"}
            size="small"
            sx={{ bgcolor: isActive ? "#16a34a" : "#64748b", color: "#fff", fontWeight: 700 }}
          />
        </Stack>
      </Stack>

      {/* Top: gallery + investment card */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                height: { xs: 240, sm: 320, md: 380 },
                bgcolor: "#e2e8f0",
                backgroundImage: `url(${mainUrl || noImgPlaceholder})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            {thumbSources.length > 0 ? (
              <Stack direction="row" spacing={1.5} sx={{ p: 2, overflowX: "auto" }}>
                {thumbSources.map((t, idx) => (
                  <Box
                    key={t.key}
                    onClick={() => setGalleryIndex(idx)}
                    sx={{
                      width: 72,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      border: galleryIndex === idx ? `3px solid ${TEAL}` : "2px solid #e2e8f0",
                      backgroundImage: `url(${t.url || noImgPlaceholder})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                ))}
              </Stack>
            ) : null}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: "1px solid #e2e8f0", height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              Token availability
            </Typography>
            <Typography variant="body2" fontWeight={700} color="#0f172a" sx={{ mb: 1 }}>
              {formatTokens(available)} of {formatTokens(totalSupply)} tokens available
            </Typography>
            <LinearProgress
              variant="determinate"
              value={soldPct}
              sx={{
                height: 8,
                borderRadius: 999,
                bgcolor: "#e2e8f0",
                mb: 2,
                "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: TEAL },
              }}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ bgcolor: "#e8f8f5", borderRadius: 2, px: 2, py: 1.5, mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <GroupsOutlined sx={{ color: TEAL_DARK }} />
                <Typography variant="body2" fontWeight={600}>
                  Current Investors
                </Typography>
              </Stack>
              <Typography fontWeight={800}>{asset?.investmentProgress?.currentInvestors || 0}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Minimum Investment
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ mb: 2 }}>
              SAR {formatSar(financial.minimumInvestment)}
            </Typography>
            <Button
              ref={setPrimaryInvestBtn}
              fullWidth
              variant="contained"
              endIcon={<OpenInNew />}
              onClick={() => setInvestOpen(true)}
              sx={{
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: TEAL,
                "&:hover": { bgcolor: TEAL_DARK },
              }}
            >
              Invest Now
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleManageDocuments}
              sx={{
                mt: 1.5,
                py: 1.25,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                borderColor: "#cbd5e1",
                color: "#334155",
              }}
            >
              Manage Documents
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block", lineHeight: 1.4 }}>
              By investing, you agree to our Terms of Service and acknowledge the investment risks.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
        {[
          { label: "Expected ROI", value: financial.expectedROI != null ? `${financial.expectedROI}%` : "—", bg: "#e8f4fc", color: "#0369a1" },
          { label: "Rental Yield", value: financial.rentalYield != null ? `${financial.rentalYield}%` : "—", bg: "#ecfdf5", color: "#047857" },
          {
            label: "Per Token",
            value: tokenEconomics.tokenPriceUSD != null ? `SAR ${formatSar(tokenEconomics.tokenPriceUSD)}` : "—",
            bg: "#f3e8ff",
            color: "#7c3aed",
          },
          { label: "Asset Value", value: `SAR ${formatSar(financial.totalAssetValue)}`, bg: "#fff7ed", color: "#c2410c" },
        ].map((cell) => (
          <Grid size={{ xs: 6, sm: 3 }} key={cell.label}>
            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: cell.bg, border: "1px solid rgba(0,0,0,0.04)", height: "100%" }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: cell.color }}>
                {cell.label}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a", mt: 0.5 }}>
                {cell.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Tabs + content */}
      <Box ref={detailTabsSectionRef} sx={{ width: "100%" }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              {visibleDetailTabs.map(({ id, label, Icon }) => {
                const active = tab === id;
                return (
                  <Button
                    key={id}
                    fullWidth
                    onClick={() => setTab(id)}
                    startIcon={<Icon sx={{ fontSize: 20 }} />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1.5,
                      px: 2,
                      borderRadius: 0,
                      fontWeight: 600,
                      color: active ? TEAL_DARK : "#64748b",
                      bgcolor: active ? "rgba(0,191,165,0.12)" : "transparent",
                      borderLeft: active ? `4px solid ${TEAL}` : "4px solid transparent",
                      "&:hover": { bgcolor: active ? "rgba(0,191,165,0.16)" : "#f8fafc" },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e2e8f0", minHeight: 360 }}>
              {tab === "overview" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    About this Asset
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {basic.description || "No description provided."}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 1 }}>
                    Investment Highlights
                  </Typography>
                  <Stack spacing={1.25}>
                    {[
                      financial.expectedROI != null && `Projected ${financial.expectedROI}% expected ROI`,
                      financial.minimumInvestment != null &&
                      `Fractional ownership from SAR ${formatSar(financial.minimumInvestment)} minimum`,
                    ]
                      .filter(Boolean)
                      .map((text, i) => (
                        <Paper
                          key={i}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderColor: "#e2e8f0",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            bgcolor: "#fafafa",
                          }}
                        >
                          <CheckCircleOutline sx={{ color: TEAL, mt: 0.15 }} />
                          <Typography variant="body2">{text}</Typography>
                        </Paper>
                      ))}
                  </Stack>
                  {!financial.expectedROI && !financial.minimumInvestment ? (
                    <Typography variant="body2" color="text.secondary">
                      More highlights will appear as asset data is completed.
                    </Typography>
                  ) : null}
                </Stack>
              )}

              {tab === "features" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    Features &amp; amenities
                  </Typography>
                  {features.length ? (
                    <Grid container spacing={1.5}>
                      {features.map((f) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f}>
                          <Paper
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: "#e8f8f5",
                              border: "1px solid rgba(0,191,165,0.2)",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <CheckCircleOutline sx={{ color: TEAL }} />
                            <Typography fontWeight={600}>{f}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="text.secondary">No features listed.</Typography>
                  )}
                </Stack>
              )}

              {tab === "specifications" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    Property specifications
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: "Property type", value: specs.propertyType },
                      {
                        label: "Size",
                        value:
                          specs.sizeSqFt != null
                            ? `${Number(specs.sizeSqFt).toLocaleString()} ${specs.sizeUnit || "sq ft"}`
                            : null,
                      },
                    ]
                      .filter((x) => x.value)
                      .map((row) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={row.label}>
                          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              {row.label}
                            </Typography>
                            <Typography fontWeight={700}>{row.value}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                  </Grid>
                  {!specs.propertyType && specs.sizeSqFt == null ? (
                    <Typography color="text.secondary">No specifications provided.</Typography>
                  ) : null}
                </Stack>
              )}

              {tab === "location" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    Location
                  </Typography>
                  {cleanLocation(asset.location) || formatAddress(basic.completeAddress) ? (
                    <Typography variant="body2" color="text.secondary">
                      {cleanLocation(asset.location) || formatAddress(basic.completeAddress)}
                    </Typography>
                  ) : null}
                  {basic.completeAddress ? (
                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" gutterBottom>
                        Full address
                      </Typography>
                      <Typography variant="body2">{formatAddress(basic.completeAddress)}</Typography>
                      {basic.completeAddress.zipCode ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          ZIP: {basic.completeAddress.zipCode}
                        </Typography>
                      ) : null}
                    </Paper>
                  ) : null}
                </Stack>
              )}

              {tab === "documents" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    Asset Documents
                  </Typography>
                  {documents.length ? (
                    <Stack spacing={1.5}>
                      {documents.map((doc, idx) => {
                        const href = resolveInvestorMediaUrl(doc.fileUrl);
                        const title =
                          (doc.type === "titleDeed"
                            ? "Title Deed"
                            : doc.type === "legalDocument"
                              ? "Legal document"
                              : doc.type === "spvDocument"
                                ? "SPV document"
                                : doc.type === "regaCertificate"
                                  ? "Rega Certificate"
                                  : doc.type === "valuationReport"
                                    ? "Valuation Report" 
                                    : doc.type || `Document ${idx + 1}`);
                        return (
                          <Paper
                            key={doc._id || idx}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <DescriptionOutlined sx={{ color: "#0284c7" }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography fontWeight={700}>{title}</Typography>
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                {/* <Chip label={documentTypeLabel(doc.type)} size="small" variant="outlined" /> */}
                                {doc.fileType ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {String(doc.fileType).toUpperCase()}
                                  </Typography>
                                ) : null}
                                {doc?.fileSize
                                  ? doc.fileSize >= 1024 * 1024
                                    ? <Typography variant="caption" color="text.secondary">· {(doc.fileSize / (1024 * 1024)).toFixed(1)} MB</Typography>
                                    : <Typography variant="caption" color="text.secondary">· {Math.round(doc.fileSize / 1024)} KB</Typography>
                                  : null}
                              </Stack>
                            </Box>
                            {href ? (
                              <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600}>
                                Open
                              </Link>
                            ) : null}
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No documents available.</Typography>
                  )}
                </Stack>
              )}

              {tab === "blockchain" && mintTxHash && (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Blockchain
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      On-chain deployment details for this asset.
                    </Typography>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: "#e2e8f0" }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <TagOutlined sx={{ color: TEAL_DARK, mt: 0.25, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Transaction Hash
                        </Typography>
                        <Typography
                          component="a"
                          href={`https://etherscan.io/tx/${mintTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          fontWeight={600}
                          fontSize={14}
                          sx={{ display: "block", wordBreak: "break-all", fontFamily: "monospace", color: TEAL_DARK, mt: 0.5, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                          {mintTxHash}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Stack>
              )}

              {tab === "verification" && (
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>
                    KYC &amp; Compliance Verification
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verification status for this asset and related compliance checks.
                  </Typography>
                  {[
                    { key: "nafath", title: "Nafath Verification", subtitle: "Saudi National ID verification" },
                    { key: "rega", title: "REGA Verification", subtitle: "Real Estate General Authority" },
                    { key: "vara", title: "VARA Verification", subtitle: "Virtual Assets Regulatory Authority" },
                    { key: "taufeeq", title: "Valuation Report Verification", subtitle: "Asset documentation & compliance" },
                  ]
                    .filter((row) => row.key !== "rega" || isRealEstate)
                    .map((row) => {
                      const status = verification[row.key] || "—";
                      const ok = String(status).toLowerCase() === "approved";
                      const isVara = row.key === "vara";
                      return (
                        <Paper
                          key={row.key}
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}
                        >
                          <Box>
                            <Typography fontWeight={700}>{row.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {row.subtitle}
                            </Typography>
                          </Box>
                          <Chip
                            label={status}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: isVara && ok ? "#f3e8ff" : ok ? "#dcfce7" : "#f1f5f9",
                              color: isVara && ok ? "#7c3aed" : ok ? "#166534" : "#475569",
                            }}
                          />
                        </Paper>
                      );
                    })}
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "#e0f2fe", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <InfoOutlined sx={{ color: "#0369a1" }} />
                    <Box>
                      <Typography fontWeight={700} color="#0c4a6e">
                        Complete verifications to unlock full trading capabilities
                      </Typography>
                      <Typography variant="caption" color="#0369a1" sx={{ display: "block", mt: 0.5 }}>
                        Status values come from the platform. Some checks may depend on asset type and jurisdiction.
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {showFloatingInvest && (
        <Button
          variant="contained"
          endIcon={<OpenInNew />}
          onClick={() => setInvestOpen(true)}
          sx={{
            position: "fixed",
            bottom: 28,
            right: 28,
            zIndex: 1200,
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            py: 1.25,
            borderRadius: 2,
            bgcolor: TEAL,
            boxShadow: "0 4px 20px rgba(0,191,165,0.45)",
            "&:hover": { bgcolor: TEAL_DARK },
          }}
        >
          Invest Now
        </Button>
      )}

      <InvestorInvestModal
        open={investOpen}
        onClose={() => setInvestOpen(false)}
        assetId={asset._id}
        ownerId={asset.createdBy?._id}
        assetName={basic.assetName || "—"}
        pricePerTokenSar={Number(tokenEconomics.tokenPriceUSD) || 0}
        minimumInvestmentSar={Number(financial.minimumInvestment) || 0}
        availableTokens={available}
        stripeAccountId={asset.stripeAccountId || asset.createdBy?.stripeAccountId}
        onSuccess={loadAsset}
      />
    </Box>
  );
}
